const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://brilliant-connect-admin.arafinahmed.com/api';

// Login
export const login = async (email, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    // Store auth data
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('role', data.role);
    localStorage.setItem('email', data.email || data.user?.email);

    // Store permissions (userPermissions for consistency with other code)
    if (data.user?.permissions) {
      localStorage.setItem('userPermissions', JSON.stringify(data.user.permissions));
      localStorage.setItem('permissions', JSON.stringify(data.user.permissions)); // Keep both for backward compatibility
    }

    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// Logout
export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('role');
  localStorage.removeItem('email');
  localStorage.removeItem('permissions');
  localStorage.removeItem('userPermissions');
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

// Get current user role
export const getUserRole = () => {
  return localStorage.getItem('role');
};

// Get current user email
export const getUserEmail = () => {
  return localStorage.getItem('email');
};

// Get user permissions
export const getUserPermissions = () => {
  const perms = localStorage.getItem('userPermissions') || localStorage.getItem('permissions');
  return perms ? JSON.parse(perms) : {};
};

// Get permissions (alias for backward compatibility)
export const getPermissions = () => {
  return getUserPermissions();
};

// Check if user has specific permission
export const hasPermission = (resource, action = 'view') => {
  const role = getUserRole();

  // Super admin has all permissions
  if (role === 'admin') return true;

  const permissions = getUserPermissions();

  // Check if permissions exist and have the specific action
  return permissions[resource]?.[action] === true;
};

// Get auth headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

// Admin APIs
export const getAllUsers = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/admin/users`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch users');
    }

    return data;
  } catch (error) {
    console.error('Get users error:', error);
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/admin/create-user`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to create user');
    }

    return data;
  } catch (error) {
    console.error('Create user error:', error);
    throw error;
  }
};

export const updateUserPermissions = async (userId, permissions) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/admin/users/${userId}/permissions`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ permissions }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update permissions');
    }

    return data;
  } catch (error) {
    console.error('Update permissions error:', error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/admin/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete user');
    }

    return data;
  } catch (error) {
    console.error('Delete user error:', error);
    throw error;
  }
};